import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { EnaioImportOptions } from 'src/modules/enaio/shared/EnaioImportOptions';
import { EnaioObjectDef } from 'src/modules/enaio/shared/EnaioObjectDef';
import { EnaioObjectId } from 'src/modules/enaio/shared/EnaioObjectId';
import { EnaioObjectType } from 'src/modules/enaio/shared/EnaioObjectType';
import { EnaioObjectTypeImportParams } from 'src/modules/enaio/shared/EnaioObjectTypeImportParams';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { DhTools } from '../../models/dh-tools.model';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';

@Component({
  selector: 'app-enaio-import-options',
  templateUrl: './enaio-import-options.component.html',
  styleUrl: './enaio-import-options.component.scss'
})
export class EnaioImportOptionsComponent {

    app = inject(MainAppService);

    @Input()
    def: EnaioObjectDef;

    _options: EnaioImportOptions;

    form: FrontendFormDefinition;
    forms: FrontendFormDefinition[] = [];
    tables: TableData[] = [];

    @Output()
    onAction = new EventEmitter<boolean>();
    @Output()
    optionsChange = new EventEmitter<EnaioImportOptions>();


    @Input()
    set options(value: EnaioImportOptions) {
        this._options = value;

        this.form = value == null ? null : new FrontendFormDefinition([
            new FrontendFieldDefinition("singleStep", "Single Step", FrontendFieldType.checkBox, { value: value.singleStep}),            
            new FrontendFieldDefinition("statusDir", "Verzeichnis für Statusdateien (leer = Exportverzeichnis)", FrontendFieldType.text, { value: value.statusDir })
        ]);

        this.forms = value == null ? [] : value.archives.map(archive => new FrontendFormDefinition([
            new FrontendFieldDefinition("import", "Importieren", FrontendFieldType.checkBox, { value: archive.import }),
            new FrontendFieldDefinition("includeChildren", "Unterlemente importieren", FrontendFieldType.checkBox, { value: archive.includeChildren }),
            new FrontendFieldDefinition("documents", "Dokumente importieren", FrontendFieldType.checkBox, { value: archive.documents })
        ]));

        this.tables = value == null ? [] : value.archives.map(archive => {
            let cab = this.def.cabinets.find(cab2 => cab2.internalName == archive.archive.internalName);
            let otListItems = cab.objectTypes.map(ot => new FrontendFieldListItem(ot.internalName, ot.name));
            return new TableData([
                new TableColumn("import", "Importieren", TableCellType.yesNo, { autoWidth: 1, editable: true }),
                new TableColumn("objectType", "Quell-Objekttyp", TableCellType.text, { autoWidth: 20 }),
                new TableColumn("destObjectType", "Ziel-Objekttyp", TableCellType.text, { autoWidth: 20, editable: true, editor:
                    new FrontendFieldDefinition("field", "Feld", FrontendFieldType.comboBox, { dropdownEditable: false, listItems: otListItems}) }),
                new TableColumn("fixedParentId", "Fester Standort (ID)", TableCellType.int, { autoWidth: 10, editable: true }),
                new TableColumn("fieldNameSourceObjectId", "Quell-Objekt-ID in dieses Feld einfügen (interner Name)", TableCellType.text, { autoWidth: 20, editable: true }),
                new TableColumn("insertOnly", "Nur einfügen, nicht aktualisieren", TableCellType.yesNo, { autoWidth: 1, editable: true }),
                new TableColumn("keyFields", "Schlüsselfelder (interne Namen durch ; getrennt)", TableCellType.text, { autoWidth: 30, editable: true/*, editor: 
                    (event) => new FrontendFieldDefinition("field", "Feld", FrontendFieldType.comboBoxMulti, { listItems: (event.row.raw as EnaioObjectTypeImportParams).objectType.getAllFields().map(field => new FrontendFieldListItem(EnaioObjectId.byInternalName(field.internalName), field.name))})
                */}),            
                new TableColumn("keyFieldsAll", "Alle Felder sind Schlüsselfeld", TableCellType.yesNo, { autoWidth: 1, editable: true })
            ], archive.objectTypes.map(ot => new TableRow("", ot, {
                import: ot.import,
                objectType: ot.objectType.name,
                destObjectType: ot.destObjectType == null ? null : ot.destObjectType.internalName,
                fixedParentId: ot.fixedParentId,
                fieldNameSourceObjectId: ot.fieldNameSourceObjectId,
                insertOnly: ot.insertOnly,
                keyFields: ot.keyFields != null ? Utils.arrayItemsToString(ot.keyFields, ";", kf => kf.internalName) : "",
                keyFieldsAll: ot.keyFieldsAll
            })));
        });
    }

    fromGui(): void {
        this.form.get(this._options);
        for (let i = 0; i < this._options.archives.length; i++) {
            this.forms[i].get(this._options.archives[i]);
            for (let row of this.tables[i].rows) {
                let ot = row.raw as EnaioObjectTypeImportParams;
                ot.import = row.values.import;
                ot.destObjectType = EnaioObjectId.byInternalName(row.values.destObjectType as string);
                ot.fixedParentId = row.values.fixedParentId;
                ot.fieldNameSourceObjectId = row.values.fieldNameSourceObjectId;
                ot.insertOnly = row.values.insertOnly;
                ot.keyFields = Utils.split(row.values.keyFields, ";").map(fieldName => EnaioObjectId.byInternalName(fieldName)),
                ot.keyFieldsAll = row.values.keyFieldsAll;
            }
        }
    }

    import(): void {
        this.fromGui();
        this.onAction.emit(true);
    }
    
    async saveConfig(): Promise<void> {
        this.fromGui();
        let fileName = await DhTools.saveDialog("importConfig.json");
        if (!Utils.isNoe(fileName)) {
            await DhTools.backendCall("api/enaio/importSaveConfig", { fileName, options: Utils.toJson(this._options)}).getText();
            let config = {
                "source": {
                    type: "EnaioExport",
                    importOptionsFileName: Utils.replaceAll(fileName, "\\\\", "/"),
                    exportDir: "<TODO>"
                },
                workers: [
                  {
                    type: "EnaioImport",
                    id: "{{id}}"
                  }
                ]
              }
            this.app.messageDialog.textArea("DH-Konfiguration für den Export", Utils.toJson(config, true));
        }
    }

    async loadConfig(): Promise<void> {
        this.fromGui();
        let fileName = await DhTools.openDialog("exportConfig.json");
        if (!Utils.isNoe(fileName)) {
            this.options = await DhTools.backendCall("api/enaio/importLoadConfig", { fileName, options: Utils.toJson(this._options)}).get(EnaioImportOptions);
            this.optionsChange.emit(this.options);
        }
    }

    cancel(): void {
        this.onAction.emit(false);
    }
}
